import type { FC } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Focusable, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

export interface SendStopButtonProps {
  /** Whether the agent is currently generating (toggles stop icon + onStopGeneration). */
  isAnalyzing: boolean;
  /** Loading state — shows a spinner in the legacy variant and the `sync` icon in the skills-UI variant. */
  disabled: boolean;
  /** Prompt textarea is empty. Computed by the parent because it owns prompt state. */
  isPromptEmpty: boolean;
  isAtUsageLimit: boolean;
  onStopGeneration?: () => void;
  handleSendPrompt: () => void;
}

export const SendStopButton: FC<SendStopButtonProps> = ({
  isAnalyzing,
  disabled,
  isPromptEmpty,
  isAtUsageLimit,
  onStopGeneration,
  handleSendPrompt,
}) => {
  if (isAnalyzing) {
    return (
      <IconButton
        icon="stop"
        variant="minimal"
        size="small"
        aria-label={msg('vega.composer.stopGeneratingLabel')}
        onPress={() => onStopGeneration?.()}
      />
    );
  }

  const isSendDisabled = disabled || isPromptEmpty || isAtUsageLimit;

  let sendButtonTooltip: string | undefined;
  if (isAtUsageLimit) {
    sendButtonTooltip = 'You have reached your agent usage limit';
  } else if (isPromptEmpty && !disabled) {
    sendButtonTooltip = 'No message entered';
  }

  const sendIcon = disabled ? 'sync' : 'arrow-up-thin';
  const variant = isSendDisabled ? 'minimal' : 'default';

  if (sendButtonTooltip) {
    return (
      <TooltipTrigger delay={0}>
        <Focusable>
          <IconButton
            icon="arrow-up-thin"
            variant="minimal"
            size="small"
            aria-label={msg('vega.composer.sendPromptLabel')}
            isDisabled
          />
        </Focusable>
        <Tooltip placement="top">{sendButtonTooltip}</Tooltip>
      </TooltipTrigger>
    );
  }

  return (
    <IconButton
      icon={sendIcon}
      variant={variant}
      size="small"
      aria-label={msg('vega.composer.sendPromptLabel')}
      onPress={() => handleSendPrompt()}
      isDisabled={isSendDisabled}
    />
  );
};
